/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { TriggerShape } from "../../core/enums";
import { EventProxy } from "../../core/event-proxy";
import type { IAnimatable, IAnimation } from "../../core/ianimatable";
import type { IHittable } from "../../core/ihittable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import { TriggerAnimation } from "./trigger-animation";
import type { TriggerData } from "./trigger-data";
import { TriggerHitCircle } from "./trigger-hit-circle";
import { TriggerHitGenerator } from "./trigger-hit-generator";
import { TriggerState } from "./trigger-state";

/**
 * A trigger (a lane switch or a rollover). It fires a hit event when a ball
 * enters and an unhit event when the ball leaves.
 */
export class Trigger extends Item<TriggerData> implements IHittable, IAnimatable {
  private readonly state: TriggerState;
  private readonly hitGenerator: TriggerHitGenerator;

  private hits?: HitObject[];
  private animation?: TriggerAnimation;

  public constructor(data: TriggerData) {
    super(data);
    this.state = new TriggerState(
      data.getName(),
      data.isVisible && data.shape !== TriggerShape.TriggerNone,
    );
    this.hitGenerator = new TriggerHitGenerator(data);
  }

  public getState(): TriggerState {
    return this.state;
  }

  public isCollidable(): boolean {
    return true;
  }

  public setupPlayer(player: Player, table: Table): void {
    this.events = new EventProxy(this, "trigger", player.getEventSink());
    this.animation = new TriggerAnimation(this.data, this.state);
    // A trigger without an outline is a circle.
    if (
      this.data.shape === TriggerShape.TriggerStar ||
      this.data.shape === TriggerShape.TriggerButton ||
      this.data.dragPoints.length < 3
    ) {
      this.hits = [new TriggerHitCircle(this.data, this.animation, this.events, table)];
    } else {
      this.hits = this.hitGenerator.generateHitObjects(this.animation, this.events, table);
    }
  }

  public getHitShapes(): HitObject[] {
    return this.hits!;
  }

  public getAnimation(): IAnimation {
    return this.animation!;
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}
